// M5.8: the player-facing team reward card. Shows the open
// + recently-claimed rewards for the user's current team.
// Each card has a progress bar, the user's contribution
// amount, and a "Contribute" button (if open and the user
// has coins to give).
//
// Design choice: a single card that shows the top 3 rewards
// (by progress, with open first). A "View all" link takes
// the user to /rewards which lists everything on the team.
// This keeps the dashboard uncluttered when a team has many
// rewards; the dedicated page is for power users.

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMyTeamRewards } from './api';
import type { MyTeam } from '../../lib/types';
import type { TeamReward } from './types';

export function TeamRewardsCard({ team }: { team: MyTeam }) {
  const rewards = useQuery({
    queryKey: ['me-team-rewards', team.id],
    queryFn: () => getMyTeamRewards(team.id),
    // The reward pool can change any time another player
    // contributes; refresh on focus keeps the bar fresh.
    refetchOnWindowFocus: true,
  });
  const list = rewards.data ?? [];
  // Open first, then by progress descending. Cap at 3.
  const top = [...list]
    .sort((a, b) => {
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (b.status === 'open' && a.status !== 'open') return 1;
      return b.progress_pct - a.progress_pct;
    })
    .slice(0, 3);
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <p className="label">Team rewards</p>
        <Link to={`/rewards?team_id=${team.id}`} className="text-xs text-ink-muted underline">
          View all
        </Link>
      </div>
      {rewards.isPending ? (
        <p className="mt-2 text-sm text-ink-muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">
          No team rewards yet. An admin can add one from the team's admin page.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {top.map((r) => <RewardRow key={r.id} reward={r} teamId={team.id} />)}
        </ul>
      )}
    </div>
  );
}

function RewardRow({ reward, teamId }: { reward: TeamReward; teamId: string }) {
  return (
    <li className="rounded-md bg-bg-elevated p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/rewards/${reward.id}?team_id=${teamId}`}
            className="font-medium hover:underline"
          >
            {reward.name}
          </Link>
          {reward.description ? (
            <p className="mt-0.5 text-xs text-ink-muted line-clamp-2">{reward.description}</p>
          ) : null}
          <ProgressBar value={reward.contributed} max={reward.coin_cost} />
          <p className="mt-1 text-xs text-ink-dim">
            <span aria-hidden>🪙</span> {reward.contributed} / {reward.coin_cost} coins
            {reward.my_contribution && reward.my_contribution > 0 ? (
              <> · you: {reward.my_contribution}</>
            ) : null}
            {reward.status === 'claimed' ? (
              <span className="ml-2 rounded bg-accent-success/20 px-1.5 py-0.5 text-accent-success">
                Claimed
              </span>
            ) : null}
          </p>
        </div>
      </div>
    </li>
  );
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg-panel">
      <div
        className="h-full bg-gradient-to-r from-accent to-accent-success"
        style={{ width: `${pct}%` }}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        role="progressbar"
      />
    </div>
  );
}
